// src/components/AddExpenseScreen.jsx
// Nova/editar despesa com parcelas, validação, normalização de dados e UI premium responsiva.
// Endpoint: /psy/payment (POST/PUT)
// Edit via location.state.expense
// Ao salvar: volta para /finance com state { refresh: true }

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  House,
  LoaderCircle,
  Megaphone,
  Menu,
  MonitorCog,
  NotebookTabs,
  ReceiptText,
  Repeat2,
  Save,
  Sparkles,
  TrendingDown,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import { useToast } from "../context/ToastContext";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/addExpense.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const CATEGORIES = [
  {
    value: "Aluguel",
    label: "Aluguel",
    icon: House,
    description: "Consultório, sala, coworking ou espaço físico.",
    tone: "blue",
  },
  {
    value: "Equipamentos",
    label: "Equipamentos",
    icon: MonitorCog,
    description: "Materiais, computador, móveis e estrutura.",
    tone: "violet",
  },
  {
    value: "Assinaturas",
    label: "Assinaturas",
    icon: Repeat2,
    description: "Softwares, plataformas, sistemas e mensalidades.",
    tone: "cyan",
  },
  {
    value: "Marketing",
    label: "Marketing",
    icon: Megaphone,
    description: "Anúncios, identidade visual e divulgação.",
    tone: "amber",
  },
  {
    value: "Outros",
    label: "Outros",
    icon: Sparkles,
    description: "Demais custos do consultório.",
    tone: "red",
  },
];

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function pad2(value) {
  return String(value).padStart(2, "0");
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function normalizeSpaces(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalizeMultiline(value = "") {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function todayISO() {
  const date = new Date();

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`;
}

function dateToISODate(dateLike) {
  if (!dateLike) return "";

  const raw = String(dateLike).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const parsed = new Date(raw);

  if (Number.isNaN(parsed.getTime())) return "";

  return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(
    parsed.getDate()
  )}`;
}

function parseISODate(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function addMonthsKeepDay(baseDate, amount) {
  const date = new Date(baseDate);
  const originalDay = date.getDate();

  const target = new Date(
    date.getFullYear(),
    date.getMonth() + amount,
    1,
    0,
    0,
    0,
    0
  );

  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(originalDay, lastDay));

  return target;
}

function formatISODate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function formatDateBR(value) {
  const iso = dateToISODate(value);

  if (!iso) return "Não informado";

  const parts = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!parts) return "Não informado";

  return `${parts[3]}/${parts[2]}/${parts[1]}`;
}

function parseMoneyBR(value) {
  if (value === null || value === undefined || value === "") return NaN;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : NaN;
  }

  const normalized = String(value)
    .replace(/[R$\s]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : NaN;
}

function formatMoneyFromDigits(value) {
  const digits = onlyDigits(value).slice(0, 10);

  if (!digits) return "";

  const number = Number(digits) / 100;

  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoneyForInput(value) {
  const number = parseMoneyBR(value);

  if (!Number.isFinite(number)) return "";

  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCurrencyBR(value) {
  const number = parseMoneyBR(value);

  if (!Number.isFinite(number)) return "R$ 0,00";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(number);
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function splitInstallments(total, count) {
  const totalCents = Math.round(roundMoney(total) * 100);
  const base = Math.floor(totalCents / count);
  let remainder = totalCents - base * count;

  return Array.from({ length: count }, () => {
    const cents = base + (remainder > 0 ? 1 : 0);
    remainder -= remainder > 0 ? 1 : 0;

    return cents / 100;
  });
}

function getCategoryInfo(category) {
  return CATEGORIES.find((item) => item.value === category) || CATEGORIES[CATEGORIES.length - 1];
}

async function readErrorMessage(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const json = await response.json();
      return json?.message || JSON.stringify(json);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function Field({ label, required, error, hint, children, className = "" }) {
  return (
    <div className={`exp-field ${className} ${error ? "exp-field--error" : ""}`}>
      <div className="exp-field__top">
        <label className="exp-label">
          {label}
          {required ? <span className="exp-req">*</span> : null}
        </label>

        {hint ? <span className="exp-hint">{hint}</span> : null}
      </div>

      {children}

      {error ? (
        <div className="exp-err" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function CategoryCard({
  option,
  active,
  disabled,
  onSelect,
}) {
  const Icon = option.icon;

  return (
    <button
      className={`exp-category-card exp-category-card--${option.tone} ${
        active
          ? "exp-category-card--active"
          : ""
      }`}
      type="button"
      onClick={() =>
        onSelect(option.value)
      }
      disabled={disabled}
      aria-pressed={active}
    >
      <span className="exp-category-card__icon">
        <Icon
          size={20}
          aria-hidden="true"
        />
      </span>

      <span className="exp-category-card__content">
        <strong>{option.label}</strong>
        <small>
          {option.description}
        </small>
      </span>
    </button>
  );
}

function InstallmentCard({
  parcela,
  index,
  error,
  onValueChange,
  onDueDateChange,
  disabled,
}) {
  return (
    <article className={`exp-parcela ${error ? "exp-parcela--error" : ""}`}>
      <div className="exp-parcela__head">
        <div>
          <span className="exp-parcela__number-icon">
            <ReceiptText
              size={15}
              aria-hidden="true"
            />
          </span>

          <span>Parcela</span>
          <strong>{parcela.numero}</strong>
        </div>

        <small>
          <CalendarDays
            size={14}
            aria-hidden="true"
          />
          {formatDateBR(
            parcela.vencimentoISO
          )}
        </small>
      </div>

      <div className="exp-parcela__row">
        <Field label="Valor" error={error?.valor} hint="R$">
          <input
            className="exp-input"
            type="text"
            inputMode="numeric"
            value={parcela.valorText}
            onChange={(event) => onValueChange(index, event.target.value)}
            placeholder="0,00"
            disabled={disabled}
          />
        </Field>

        <Field label="Vencimento" error={error?.vencimento}>
          <input
            className="exp-input"
            type="date"
            value={parcela.vencimentoISO}
            onChange={(event) => onDueDateChange(index, event.target.value)}
            disabled={disabled}
          />
        </Field>
      </div>
    </article>
  );
}

export default function AddExpenseScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const pageRef = useRef(null);

  const editingExpense = useMemo(
    () => location.state?.expense || location.state?.args || null,
    [location.state]
  );

  const isEdit = Boolean(editingExpense && (editingExpense._id || editingExpense.id));

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expenseId, setExpenseId] = useState(null);

  const [amount, setAmount] = useState("");
  const [installments, setInstallments] = useState("1");
  const [category, setCategory] = useState("Aluguel");
  const [dateISO, setDateISO] = useState("");
  const [description, setDescription] = useState("");
  const [parcelas, setParcelas] = useState([]);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [parcelErrors, setParcelErrors] = useState([]);

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const selectedCategory = useMemo(
    () => getCategoryInfo(category),
    [category]
  );

  const SelectedCategoryIcon =
    selectedCategory.icon;

  const amountNumber = useMemo(
    () => parseMoneyBR(amount),
    [amount]
  );

  const parcelasTotal = useMemo(() => {
    return (parcelas || []).reduce((sum, parcela) => {
      const valor = parseMoneyBR(parcela.valorText);
      return sum + (Number.isFinite(valor) ? valor : 0);
    }, 0);
  }, [parcelas]);

  const difference = useMemo(() => {
    if (!Number.isFinite(amountNumber)) return 0;
    return roundMoney(parcelasTotal - amountNumber);
  }, [parcelasTotal, amountNumber]);

  const completion = useMemo(() => {
    const checks = [
      Number.isFinite(amountNumber) && amountNumber > 0,
      Boolean(installments) && Number(installments) > 0,
      CATEGORIES.some((item) => item.value === category),
      Boolean(dateISO) && Boolean(parseISODate(dateISO)),
      parcelas.length > 0,
    ];

    if (description) {
      checks.push(normalizeSpaces(description).length >= 3);
    }

    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [amountNumber, installments, category, dateISO, parcelas.length, description]);

  const title = isEdit ? "Editar despesa" : "Adicionar despesa";

  useEffect(() => {
    if (!isEdit) {
      setDateISO(todayISO());
      return;
    }

    const id = editingExpense?._id || editingExpense?.id || editingExpense?.paymentId || null;
    setExpenseId(id);

    const valorTotal = editingExpense?.valorTotal ?? editingExpense?.total ?? editingExpense?.valor ?? "";
    setAmount(formatMoneyForInput(valorTotal));

    setCategory(editingExpense?.servicoId || editingExpense?.categoria || "Aluguel");
    setDescription(editingExpense?.descricao || "");

    const baseDate =
      editingExpense?.dataCadastro ||
      editingExpense?.data ||
      editingExpense?.createdAt ||
      editingExpense?.vencimento ||
      null;

    setDateISO(dateToISODate(baseDate) || todayISO());

    if (Array.isArray(editingExpense?.parcelas) && editingExpense.parcelas.length) {
      setInstallments(String(editingExpense.parcelas.length));

      setParcelas(
        editingExpense.parcelas.map((parcela, index) => ({
          numero: parcela.numero || index + 1,
          valorText: formatMoneyForInput(parcela.valor ?? parcela.valorParcela ?? ""),
          vencimentoISO: dateToISODate(parcela.vencimento || parcela.dataVencimento) || "",
        }))
      );
    } else {
      setInstallments("1");
      setParcelas([]);
    }
  }, [isEdit, editingExpense]);

  const showMessage = useCallback(
    (type, text, options = {}) => {
      const tone = [
        "success",
        "warning",
        "error",
        "info",
      ].includes(type)
        ? type
        : "info";

      const defaultTitles = {
        success: "Operação concluída",
        warning: "Revise as informações",
        error: "Não foi possível concluir",
        info: "Informação",
      };

      toast[tone]({
        title:
          options.title ||
          defaultTitles[tone],
        message: text || "",
        details: Array.isArray(
          options.details
        )
          ? options.details
          : [],
        actionLabel:
          options.actionLabel || "",
        onAction:
          typeof options.onAction ===
          "function"
            ? options.onAction
            : null,
        duration:
          options.duration !== undefined
            ? options.duration
            : options.actionLabel
              ? 0
              : 5200,
      });
    },
    [toast]
  );

  const focusFirstError = (selector = ".exp-err") => {
    window.setTimeout(() => {
      const root = pageRef.current || document;
      const errorElement = root.querySelector(selector);
      const target =
        errorElement?.closest(".exp-field, .exp-parcela, .exp-card, section") ||
        errorElement;

      if (!target) return;

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      const focusable = target.querySelector?.(
        "input, textarea, select, button:not([disabled])"
      );

      if (focusable && typeof focusable.focus === "function") {
        focusable.focus({ preventScroll: true });
      }
    }, 80);
  };

  const authOrRedirect = () => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      showMessage(
        "error",
        "Sessão expirada. Faça login novamente.",
        {
          title: "Sessão encerrada",
          duration: 6500,
        }
      );

      navigate(
        "/login",
        {
          replace: true,
        }
      );
      return null;
    }

    return { token, userId };
  };

  const setAmountSafe = (value) => {
    setAmount(formatMoneyFromDigits(value));
    setParcelas([]);
    setParcelErrors([]);
    setErrors((prev) => ({ ...prev, amount: "" }));
  };

  const setInstallmentsSafe = (value) => {
    const digits = onlyDigits(value).slice(0, 2);
    setInstallments(digits);
    setParcelas([]);
    setParcelErrors([]);
    setErrors((prev) => ({ ...prev, installments: "" }));
  };

  const setDateSafe = (value) => {
    setDateISO(value);
    setParcelas([]);
    setParcelErrors([]);
    setErrors((prev) => ({ ...prev, date: "" }));
  };

  const setCategorySafe = (value) => {
    setCategory(value);
    setErrors((prev) => ({ ...prev, category: "" }));
  };

  const setDescriptionSafe = (value) => {
    setDescription(value);
    setErrors((prev) => ({ ...prev, description: "" }));
  };

  const validateBase = () => {
    const nextErrors = {};

    const total = parseMoneyBR(amount);
    const count = Number.parseInt(installments || "0", 10);

    if (!String(amount || "").trim()) {
      nextErrors.amount = "Informe o valor total da despesa.";
    } else if (!Number.isFinite(total) || total <= 0) {
      nextErrors.amount = "Informe um valor maior que zero.";
    }

    if (!count || count <= 0) {
      nextErrors.installments = "Informe a quantidade de parcelas.";
    } else if (count > 60) {
      nextErrors.installments = "Limite máximo: 60 parcelas.";
    }

    if (!CATEGORIES.some((item) => item.value === category)) {
      nextErrors.category = "Selecione uma categoria válida.";
    }

    if (!dateISO) {
      nextErrors.date = "Informe a data base da despesa.";
    } else if (!parseISODate(dateISO)) {
      nextErrors.date = "Data inválida.";
    }

    if (description && normalizeSpaces(description).length < 3) {
      nextErrors.description = "A descrição precisa ter pelo menos 3 caracteres.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showMessage("error", "Revise os campos destacados antes de continuar.");
      focusFirstError(".exp-err");
      return false;
    }

    return true;
  };

  const gerarParcelasLocal = () => {
    if (!validateBase()) return [];

    const baseDate = parseISODate(dateISO);
    const total = parseMoneyBR(amount);
    const count = Math.max(1, Number.parseInt(installments || "1", 10) || 1);
    const values = splitInstallments(total, count);

    return values.map((valorParcela, index) => {
      const vencimento = addMonthsKeepDay(baseDate, index);

      return {
        numero: index + 1,
        valorText: formatMoneyForInput(valorParcela),
        vencimentoISO: formatISODate(vencimento),
      };
    });
  };

  const generateParcelas = () => {
    const generated = gerarParcelasLocal();

    if (!generated.length) return;

    setParcelas(generated);
    setParcelErrors([]);
    showMessage("success", `${generated.length} parcela(s) gerada(s) com sucesso.`);
  };

  const setParcelaValue = (index, value) => {
    setParcelas((prev) =>
      prev.map((parcela, currentIndex) =>
        currentIndex === index
          ? { ...parcela, valorText: formatMoneyFromDigits(value) }
          : parcela
      )
    );

    setParcelErrors((prev) =>
      prev.map((item, currentIndex) =>
        currentIndex === index ? { ...item, valor: "" } : item
      )
    );
  };

  const setParcelaDueDate = (index, value) => {
    setParcelas((prev) =>
      prev.map((parcela, currentIndex) =>
        currentIndex === index ? { ...parcela, vencimentoISO: value } : parcela
      )
    );

    setParcelErrors((prev) =>
      prev.map((item, currentIndex) =>
        currentIndex === index ? { ...item, vencimento: "" } : item
      )
    );
  };

  const validateParcelas = (list) => {
    const nextParcelErrors = [];
    let valid = true;

    const built = (list || []).map((parcela, index) => {
      const parcelaError = {};
      const valor = parseMoneyBR(parcela.valorText);
      const vencimento = parcela.vencimentoISO;

      if (!Number.isFinite(valor) || valor <= 0) {
        parcelaError.valor = "Informe um valor maior que zero.";
        valid = false;
      }

      if (!vencimento || !parseISODate(vencimento)) {
        parcelaError.vencimento = "Informe um vencimento válido.";
        valid = false;
      }

      nextParcelErrors[index] = parcelaError;

      return {
        numero: parcela.numero || index + 1,
        valor: Number.isFinite(valor) ? roundMoney(valor) : 0,
        vencimento,
      };
    });

    setParcelErrors(nextParcelErrors);

    if (!valid) {
      showMessage("error", "Revise os valores e vencimentos das parcelas.");
      focusFirstError(".exp-parcela--error .exp-err, .exp-err");
      return { ok: false, parcelas: [] };
    }

    return { ok: true, parcelas: built };
  };

  const buildPayload = (parcelasPayload) => {
    const totalFinal = roundMoney(
      parcelasPayload.reduce((sum, parcela) => sum + (Number(parcela.valor) || 0), 0)
    );

    return {
      clienteId: "despesa",
      servicoId: category,
      metodoPagamento: "Manual",
      valorTotal: totalFinal,
      descricao: normalizeMultiline(description),
      parcelas: parcelasPayload,
    };
  };

  const salvarDespesa = async () => {
    if (!validateBase()) return;

    const auth = authOrRedirect();
    if (!auth) return;

    let parcelasParaSalvar = parcelas;

    if (!parcelasParaSalvar.length) {
      parcelasParaSalvar = gerarParcelasLocal();

      if (!parcelasParaSalvar.length) return;

      setParcelas(parcelasParaSalvar);
    }

    const built = validateParcelas(parcelasParaSalvar);

    if (!built.ok) return;

    const totalGerado = roundMoney(
      built.parcelas.reduce((sum, parcela) => sum + (Number(parcela.valor) || 0), 0)
    );

    const totalInformado = roundMoney(parseMoneyBR(amount));

    if (Math.abs(totalGerado - totalInformado) > 0.02) {
      showMessage(
        "error",
        `A soma das parcelas (${formatCurrencyBR(totalGerado)}) está diferente do valor total (${formatCurrencyBR(totalInformado)}).`
      );
      focusFirstError(".exp-parcelas__diff.is-bad, .exp-parcelas, .exp-err");
      return;
    }

    const payload = buildPayload(built.parcelas);

    setSaving(true);

    try {
      const url = isEdit
        ? `${API_BASE}/psy/payment/${encodeURIComponent(expenseId)}`
        : `${API_BASE}/psy/payment`;

      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401 || response.status === 403) {
        showMessage(
          "error",
          "Sessão expirada. Faça login novamente.",
          {
            title: "Sessão encerrada",
            duration: 6500,
          }
        );

        navigate(
          "/login",
          {
            replace: true,
          }
        );
        return;
      }

      if (!response.ok) {
        const message = await readErrorMessage(response);
        showMessage("error", message);
        return;
      }

      showMessage(
        "success",
        isEdit
          ? "Despesa atualizada com sucesso."
          : "Despesa salva com sucesso.",
        {
          title: isEdit
            ? "Despesa atualizada"
            : "Despesa adicionada",
        }
      );

      navigate(
        "/finance",
        {
          replace: true,
          state: {
            refresh: true,
          },
        }
      );
    } catch (error) {
      console.error("Erro ao salvar despesa:", error);
      showMessage("error", "Falha de comunicação com a API.");
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    if (saving) return;
    navigate("/finance");
  };

  return (
    <div className="exp-shell" ref={pageRef}>
      <div className="exp-bg" aria-hidden="true">
        <span className="exp-bg__orb exp-bg__orb--one" />
        <span className="exp-bg__orb exp-bg__orb--two" />
        <span className="exp-bg__orb exp-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="exp-topbar">
        <button
          className="exp-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu
            size={19}
            aria-hidden="true"
          />
        </button>

        <div className="exp-brand">
          <img src={Logo} alt="PsyAssist" className="exp-brand__logo" />
          <div className="exp-brand__text">
            <strong>{title}</strong>
            <span>Financeiro • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="exp-top-actions">
          <button
            className="exp-btn exp-btn--ghost exp-btn--desktop"
            type="button"
            onClick={goBack}
            disabled={saving}
          >
            <ArrowLeft
              size={16}
              aria-hidden="true"
            />
            Voltar
          </button>

          <button
            className="exp-btn exp-btn--primary"
            type="button"
            onClick={salvarDespesa}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  aria-hidden="true"
                  className="exp-icon-spin"
                />
                Salvando...
              </>
            ) : (
              <>
                <Save
                  size={16}
                  aria-hidden="true"
                />
                {isEdit
                  ? "Atualizar"
                  : "Salvar"}
              </>
            )}
          </button>
        </div>
      </header>

      <main className="exp-main">
        <section className="exp-hero">
          <div>
            <span className="exp-kicker">
              <TrendingDown
                size={15}
                aria-hidden="true"
              />
              {isEdit
                ? "Atualização financeira"
                : "Nova saída"}
            </span>
            <h1>{title}</h1>
            <p>
              Registre despesas com parcelas, vencimentos e categoria para manter
              seu controle financeiro mais previsível e profissional.
            </p>
          </div>

          <aside
            className={`exp-preview-card exp-preview-card--${selectedCategory.tone}`}
          >
            <div
              className="exp-preview-card__icon"
              aria-hidden="true"
            >
              <SelectedCategoryIcon
                size={24}
              />
            </div>

            <div>
              <small>Prévia da despesa</small>
              <strong>{formatCurrencyBR(amount)}</strong>
              <span>
                {selectedCategory.label} • {parcelas.length || Number(installments || 0) || 1} parcela(s)
              </span>
            </div>
          </aside>
        </section>

        <section className="exp-page">

          <section className="exp-card">
            <div className="exp-card__head">
              <div>
                <span className="exp-card__eyebrow">
                  <NotebookTabs
                    size={14}
                    aria-hidden="true"
                  />
                  Despesa
                </span>
                <h2>Dados do lançamento</h2>
                <p>
                  Gere as parcelas automaticamente ou ajuste manualmente valores
                  e vencimentos antes de salvar.
                </p>
              </div>

              <div className="exp-score">
                <small>
                  <CheckCircle2
                    size={13}
                    aria-hidden="true"
                  />
                  Completude
                </small>
                <strong>{completion}%</strong>
                <div className="exp-score__bar">
                  <span style={{ width: `${completion}%` }} />
                </div>
              </div>
            </div>

            <form className="exp-form" onSubmit={(event) => event.preventDefault()}>
              <div className="exp-grid">
                <Field label="Valor total" required error={errors.amount} hint="R$">
                  <input
                    className="exp-input"
                    type="text"
                    inputMode="numeric"
                    value={amount}
                    onChange={(event) => setAmountSafe(event.target.value)}
                    placeholder="Digite o valor total"
                    disabled={saving}
                  />
                </Field>

                <Field label="Parcelas" required error={errors.installments} hint="máximo 60">
                  <input
                    className="exp-input"
                    type="text"
                    inputMode="numeric"
                    value={installments}
                    onChange={(event) => setInstallmentsSafe(event.target.value)}
                    placeholder="Número de parcelas"
                    disabled={saving}
                  />
                </Field>

                <Field label="Data base" required error={errors.date}>
                  <input
                    className="exp-input"
                    type="date"
                    value={dateISO}
                    onChange={(event) => setDateSafe(event.target.value)}
                    disabled={saving}
                  />
                </Field>

                <Field label="Resumo das parcelas" hint="calculado">
                  <div className="exp-total-box">
                    <span>Total das parcelas</span>
                    <strong>{formatCurrencyBR(parcelas.length ? parcelasTotal : amount)}</strong>
                  </div>
                </Field>

                <Field
                  label="Categoria"
                  required
                  error={errors.category}
                  className="exp-field--full"
                >
                  <div className="exp-category-grid">
                    {CATEGORIES.map((option) => (
                      <CategoryCard
                        key={option.value}
                        option={option}
                        active={category === option.value}
                        disabled={saving}
                        onSelect={setCategorySafe}
                      />
                    ))}
                  </div>
                </Field>

                <Field
                  label="Descrição"
                  error={errors.description}
                  hint="Opcional"
                  className="exp-field--full"
                >
                  <textarea
                    className="exp-textarea"
                    rows={4}
                    value={description}
                    onChange={(event) => setDescriptionSafe(event.target.value)}
                    onBlur={(event) =>
                      setDescriptionSafe(normalizeMultiline(event.target.value))
                    }
                    placeholder="Ex.: aluguel do consultório, assinatura de sistema, campanha de marketing..."
                    disabled={saving}
                  />
                </Field>
              </div>

              <div className="exp-actions" aria-label="Ações da despesa">
                <button
                  type="button"
                  className="exp-btn exp-btn--secondary"
                  onClick={generateParcelas}
                  disabled={saving}
                >
                  <CalendarClock
                    size={16}
                    aria-hidden="true"
                  />
                  Gerar parcelas
                </button>

                <button
                  type="button"
                  className="exp-btn exp-btn--primary"
                  onClick={salvarDespesa}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <LoaderCircle
                        size={17}
                        aria-hidden="true"
                        className="exp-icon-spin"
                      />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save
                        size={17}
                        aria-hidden="true"
                      />
                      {isEdit
                        ? "Atualizar despesa"
                        : "Salvar despesa"}
                    </>
                  )}
                </button>
              </div>

              {parcelas.length > 0 ? (
                <section className="exp-parcelas">
                  <div className="exp-parcelas__head">
                    <div>
                      <span>Parcelas</span>
                      <h2>{parcelas.length} parcela(s) gerada(s)</h2>
                      <p>
                        O valor enviado será validado pela soma das parcelas.
                      </p>
                    </div>

                    <div
                      className={`exp-parcelas__diff ${
                        Math.abs(difference) > 0.02 ? "is-bad" : "is-good"
                      }`}
                    >
                      <span>Diferença</span>
                      <strong>{formatCurrencyBR(difference)}</strong>
                    </div>
                  </div>

                  <div className="exp-parcelas__list">
                    {parcelas.map((parcela, index) => (
                      <InstallmentCard
                        key={`${parcela.numero}-${index}`}
                        parcela={parcela}
                        index={index}
                        error={parcelErrors[index]}
                        disabled={saving}
                        onValueChange={setParcelaValue}
                        onDueDateChange={setParcelaDueDate}
                      />
                    ))}
                  </div>

                  <div className="exp-note">
                    O total das parcelas deve bater com o valor total informado.
                  </div>
                </section>
              ) : null}
            </form>
          </section>
        </section>
      </main>
    </div>
  );
}
